import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Package, Truck, Home, Loader2, Download } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { formatPrice } from "@/data/products";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Success = () => {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { clearCart } = useCart();
  const [status, setStatus] = useState("checking");
  const [order, setOrder] = useState(null);

  useEffect(() => {
    let attempts = 0;
    let timer;
    const poll = async () => {
      try {
        const res = await axios.get(`${API}/payments/status/${sessionId}`);
        if (res.data.payment_status === "paid") {
          setStatus("paid");
          clearCart();
          const orderRes = await axios.get(`${API}/orders/${sessionId}`);
          setOrder(orderRes.data);
          return;
        }
      } catch (e) {
        // keep polling briefly
      }
      attempts += 1;
      if (attempts < 10) timer = setTimeout(poll, 2000);
      else setStatus("pending");
    };
    if (sessionId) poll();
    else setStatus("missing");
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <div data-testid="success-page" className="pt-32 pb-24 min-h-screen bg-cream flex items-center justify-center px-5">
      <div className="max-w-xl w-full text-center">
        {status === "checking" && (
          <div data-testid="success-loading" className="flex flex-col items-center gap-4">
            <Loader2 size={40} className="animate-spin text-rose-gold" />
            <p className="text-muted-warm text-sm">Confirmation de votre paiement…</p>
          </div>
        )}
        {(status === "paid" || status === "pending") && (
          <>
            <div className="animate-checkmark w-24 h-24 mx-auto rounded-full bg-[#2E6B4F]/10 flex items-center justify-center">
              <CheckCircle2 size={52} className="text-[#2E6B4F]" strokeWidth={1.5} />
            </div>
            <h1 data-testid="success-title" className="mt-8 font-serif text-4xl sm:text-5xl font-light text-onyx">Merci pour votre commande !</h1>
            {order && (
              <p data-testid="success-order-number" className="mt-3 font-mono text-sm tracking-widest text-terracotta">
                N° {order.order_id}
              </p>
            )}
            <p className="mt-4 text-sm text-muted-warm leading-relaxed max-w-md mx-auto">
              {order
                ? <>Un email de confirmation a été envoyé à <span className="font-semibold text-onyx">{order.customer.email}</span>. Votre rituel Relax est en préparation.</>
                : "Votre paiement est en cours de confirmation."}
            </p>
            {order && (
              <div className="mt-10 bg-white rounded-3xl border border-blush-deep p-7 text-left" data-testid="success-order-recap">
                <div className="space-y-3 mb-6">
                  {order.items.map((it) => (
                    <div key={it.lookup_key} className="flex justify-between text-sm">
                      <span className="text-onyx">{it.name} × {it.quantity}</span>
                      <span className="font-medium text-onyx">{formatPrice((it.unit_amount_cents * it.quantity) / 100)}</span>
                    </div>
                  ))}
                  <div className="flex justify-between text-sm text-muted-warm">
                    <span>{order.shipping_method}</span>
                    <span>{order.shipping_fee_cents === 0 ? "Offerte" : formatPrice(order.shipping_fee_cents / 100)}</span>
                  </div>
                  {order.discount_cents > 0 && (
                    <div className="flex justify-between text-sm text-[#2E6B4F]">
                      <span>Code promo {order.promo_code}</span>
                      <span data-testid="success-order-discount">− {formatPrice(order.discount_cents / 100)}</span>
                    </div>
                  )}
                  <div className="flex justify-between pt-3 border-t border-blush-deep">
                    <span className="font-semibold text-onyx text-sm">Total réglé ({order.payment_method === "paypal" ? "PayPal" : "Carte Bleue"})</span>
                    <span data-testid="success-order-total" className="font-serif text-2xl text-terracotta">{formatPrice(order.total_cents / 100)}</span>
                  </div>
                </div>
                <div className="border-t border-blush-deep pt-6">
                  <p className="font-mono text-[10px] tracking-widest uppercase text-muted-warm mb-5">Suivi de votre commande</p>
                  <div className="flex items-center justify-between">
                    {[
                      { icon: CheckCircle2, label: "Confirmée", active: true },
                      { icon: Package, label: "Préparation", active: true },
                      { icon: Truck, label: "Expédition", active: order.status === "shipped" },
                      { icon: Home, label: "Livraison", active: false },
                    ].map((s, i, arr) => (
                      <div key={s.label} className="flex items-center flex-1 last:flex-none">
                        <div className="flex flex-col items-center gap-1.5">
                          <div data-testid={`success-step-${i}`} className={`w-9 h-9 rounded-full flex items-center justify-center ${s.active ? "bg-terracotta text-cream" : "bg-blush text-stone-400"}`}>
                            <s.icon size={15} />
                          </div>
                          <span className={`text-[10px] ${s.active ? "text-onyx font-medium" : "text-stone-400"}`}>{s.label}</span>
                        </div>
                        {i < arr.length - 1 && <div className={`flex-1 h-px mx-2 -mt-4 ${s.active && arr[i + 1].active ? "bg-terracotta" : "bg-blush-deep"}`} />}
                      </div>
                    ))}
                  </div>
                </div>
                <div className="mt-6 rounded-2xl bg-cream px-5 py-4 text-xs text-muted-warm leading-relaxed">
                  <p className="font-semibold text-onyx mb-1">Livraison à :</p>
                  {order.customer.first_name} {order.customer.last_name} · {order.customer.address}, {order.customer.zip} {order.customer.city}, {order.customer.country}
                </div>
              </div>
            )}
            <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
              <button
                data-testid="success-invoice-button"
                onClick={() => window.print()}
                className="rounded-full border border-blush-deep bg-white px-6 py-3.5 text-sm font-medium text-onyx hover:border-rose-gold transition-colors flex items-center gap-2"
              >
                <Download size={15} /> Télécharger ma facture
              </button>
              <Link
                to="/"
                data-testid="success-home-button"
                className="rounded-full bg-onyx text-cream px-6 py-3.5 text-sm font-semibold hover:bg-terracotta transition-colors"
              >
                Retour à l'accueil
              </Link>
            </div>
          </>
        )}
        {status === "missing" && (
          <div>
            <p className="font-serif text-3xl text-onyx">Aucune commande trouvée</p>
            <Link to="/" className="mt-6 inline-block rounded-full bg-onyx text-cream px-6 py-3.5 text-sm font-semibold">Retour à l'accueil</Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default Success;
